import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismTable } from '@mylighthouse/prism-ember';
import type { CustomEmberTableColumn } from '@mylighthouse/prism-ember/types/components/prism/table';
import { REVERSE_ISO_DAYS } from 'frontend-utils/utils/constants/constant-values';
import HotelPricingAssistantTableCellsTdRoom from 'lumen/components/hotel/pricing-assistant/table/cells/td-room';
import TdMultiRoomDow from 'lumen/components/settings/recommendations/price-range/td-multi-room-dow';
import TdMultiRoomPricingRangeMinMaxLabels from 'lumen/components/settings/recommendations/td-multi-room-pricing-range-min-max-labels';
import type { RecommendationStrategy } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { getWeekDayColumns } from 'lumen/utils/pricing-assistant/recommendation-settings';

interface SettingsRecommendationsMultiRoomPriceRangeStrategyTableSignature {
  Args: {
    isEditMode: boolean;
    isSaveStrategyTaskRunning: boolean;
    roomOffsetHierarchy?: string[];
    strategy: RecommendationStrategy;
  };
}

export default class MultiRoomPriceRangeStrategyTable extends Component<SettingsRecommendationsMultiRoomPriceRangeStrategyTableSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  // eslint-disable-next-line ember/no-tracked-properties-from-args
  @tracked strategy = this.args.strategy;

  get columns(): CustomEmberTableColumn[] {
    return [
      {
        isEditing: this.args.isEditMode,
        name: '',
        strategy: this.strategy,
        tdClassNames: 'td-strategy-table',
        valuePath: 'room',
        textAlign: 'left' as const,
        minWidth: 120,
        maxWidth: 120,
        tdComponent: HotelPricingAssistantTableCellsTdRoom,
        variant: 'column' as const,
      },
      {
        width: 42,
        name: '',
        valuePath: 'min-max',
        tdClassNames: 'td-strategy-table-multi-room-min-max',
        tdComponent:
          TdMultiRoomPricingRangeMinMaxLabels as unknown as typeof Component,
      },
      ...getWeekDayColumns().map((column) => ({
        ...column,
        isEditing: this.args.isEditMode,
        name: this.intl.t(`days_short.${column.isoWeekDay}`),
        minWidth: 83,
        strategy: this.strategy,
        isSaveStrategyTaskRunning: this.args.isSaveStrategyTaskRunning,
        tdClassNames: 'td-strategy-table',
        tdComponent: TdMultiRoomDow,
        onDayOfWeekValueChange: this.onDayOfWeekValueChange.bind(this),
        textAlign: 'left' as const,
        valuePath: column.id.toString(),
      })),
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get priceRangeSettings() {
    const { settings } = this.strategy;
    return settings.settings_type === 'price_range' ? settings : null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rows() {
    type Row = {
      [dayOfWeek: number]: { maximum: number | null; minimum: number | null };
      room: { isBaseRoom: boolean; roomName: string };
      roomId: string;
    };

    const hotel = this.hotelAndMarketService.hotel;
    const { pms_leading_room_id } = hotel.caxxxPmsSettings;

    const roomNameById = hotel.pricingIntegration.pms_id_to_pms_room_name;
    const settings = this.priceRangeSettings;
    if (!settings) {
      return [];
    }
    const { overrides } = settings;

    const defaultCellValue = { maximum: null, minimum: null };
    const weekDays = Object.keys(REVERSE_ISO_DAYS).map(Number);

    return (this.args.roomOffsetHierarchy ?? []).map((roomId) => {
      const roomName = roomNameById[roomId] ?? roomId;
      const row: Row = {
        room: { roomName, isBaseRoom: pms_leading_room_id === roomName },
        roomId,
      };

      weekDays.forEach((dayOfWeek) => {
        row[dayOfWeek] = overrides[dayOfWeek]?.[roomId] ?? defaultCellValue;
      });

      return row;
    });
  }

  onDayOfWeekValueChange(
    dayOfWeek: number,
    roomId: string,
    value: number | null,
    type: 'minimum' | 'maximum',
  ): void {
    const settings = this.strategy.settings;
    if (settings.settings_type !== 'price_range') {
      return;
    }

    if (
      !settings.overrides[dayOfWeek] ||
      !settings.overrides[dayOfWeek][roomId]
    ) {
      return;
    }

    this.strategy.settings = {
      ...settings,
      overrides: {
        ...settings.overrides,
        [dayOfWeek]: {
          ...settings.overrides?.[dayOfWeek],
          [roomId]: {
            ...(settings.overrides?.[dayOfWeek]?.[roomId] ?? {}),
            [type]: value,
          },
        },
      },
    };
  }

  <template>
    <PrismTable
      @columns={{this.columns}}
      @rows={{this.rows}}
      @widthConstraint="eq-container"
    />
  </template>
}
